import GenericTable from 'modules/Tables'
import BarChart from 'modules/Tables/Dashboard/AllDashboards/BarChart'

import { IModel } from 'interfaces/model.interface'
import {
  defaultDashboardConfig,
  INavigationPages,
  ISingleNavigationPage,
} from 'interfaces/navigationPage.interface'

import {
  InfluenzaMediaModel,
  InfluenzaPressReleasesModel,
  InfluenzaTrialsModel,
} from 'config/apps/Influenza/Science360/VaccinesTherapeutics/InfluenzaModels'

import { RSVSlugs } from './RSVMenu'
import {
  RsvApprovalsNonVaccinesModel,
  RsvApprovalsVaccinesModel,
  RSVCandidatesAllCombinedModel,
  RSVCandidatesTreatmentModel,
  RSVCandidatesVaccineModel,
  RSVCombinationVaccineModel,
  RSVToolMetaNonVaccineEfficacyModel,
  RSVToolMetaVaccineEfficacyModel,
} from './RSVModels'
import barChartMessages from 'modules/Tables/Dashboard/AllDashboards/BarChart/message'
import { fetchCollection } from 'api/useCollectionData'

type RSVNavigationPage<TModel extends IModel<any>> = ISingleNavigationPage<
  RSVSlugs,
  TModel
>

const RSVCandidatesVaccine: RSVNavigationPage<
  typeof RSVCandidatesVaccineModel
> = {
  key: 'RSVCandidatesVaccine',
  path: ['candidates', 'dashboard'],
  model: {
    ...RSVCandidatesVaccineModel,
    newNote: {
      text: barChartMessages,
      newUntil: new Date(2024, 8, 25),
    },
  },
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'rsv_vaccines_and_treatments',
      airtableBase: 'lzdb',
      overrideModel: () => ({
        ...RSVCandidatesAllCombinedModel,
        customAPIFunction: (axios) => {
          const resolveData = Promise.all([
            fetchCollection(axios, 'rsv', 'candidate', {
              airtableBase: 'lzdb',
              view: 'rsv_treatments',
            }),
            fetchCollection(axios, 'rsv', 'candidate', {
              airtableBase: 'lzdb',
              view: 'rsv_vaccines',
            }),
          ]).then((d) => {
            return d[0].results
              .map((obj: any) => {
                const { treatmentType, vaccineType, ...rest } = obj
                return {
                  ...rest,
                  vaccineType: treatmentType ?? vaccineType,
                }
              })
              .concat(d[1].results)
          })

          return () => resolveData
        },
      }),
    },
    {
      name: 'Vaccines',
      airtableName: 'rsv_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'rsv_treatments',
      airtableBase: 'lzdb',
      overrideModel: RSVCandidatesTreatmentModel,
    },
    {
      name: 'Combination vaccines',
      airtableName: 'rsv_combination_vaccines',
      airtableBase: 'lzdb',
      overrideModel: RSVCombinationVaccineModel,
    },
    {
      name: 'Infant',
      airtableName: 'rsv_vaccines_infant',
      airtableBase: 'lzdb',
    },
    {
      name: 'Paediatric',
      airtableName: 'rsv_vaccines_paediatric',
      airtableBase: 'lzdb',
    },
    {
      name: 'Maternal',
      airtableName: 'rsv_vaccines_maternal',
      airtableBase: 'lzdb',
    },
    {
      name: 'Elderly',
      airtableName: 'rsv_vaccines_elderly',
      airtableBase: 'lzdb',
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    render: BarChart,
    verticalAxisLabel: 'Number of Candidates',
    filterBlacklistOptions: ['sponsors', 'alternativeNames'],
    defaultHorizontalFilterValue: {
      label: 'Phase',
      value: 'latestPhaseManual',
    },
    defaultStackByFilterValue: {
      label: 'Target Population',
      value: 'targetPopulation',
    },
    sortingOrders: {
      latestPhaseManual: [
        'Preclinical',
        'Phase 0',
        'Phase I',
        'Phase I/II',
        'Phase II',
        'Phase II/III',
        'Phase III',
        'Approved',
        'Phase IV',
        'Discontinued',
        'Paused',
        'Inactive',
        'Not Applicable',
        'Unknown',
      ],
    },
  },
}

const RSVResultsVaccines: RSVNavigationPage<
  typeof RSVToolMetaVaccineEfficacyModel
> = {
  key: 'RSVResultsVaccines',
  path: ['results', 'vaccines'],
  model: RSVToolMetaVaccineEfficacyModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'results_rsv_vaccine_trials_frontend',
      airtableBase: 'respiratory360',
    },
  ],
}
const RSVResultsNonVaccines: RSVNavigationPage<
  typeof RSVToolMetaVaccineEfficacyModel
> = {
  key: 'RSVResultsNonVaccines',
  path: ['results', 'non-vaccines'],
  model: RSVToolMetaNonVaccineEfficacyModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'results_rsv_treatments_frontend',
      airtableBase: 'respiratory360',
    },
  ],
}

const RSVTrials: RSVNavigationPage<typeof InfluenzaTrialsModel> = {
  key: 'RSVTrials',
  path: ['trials', 'all-data'],
  model: InfluenzaTrialsModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'rsv_all',
      airtableBase: 'lzdb',
    },
    {
      name: 'Vaccines',
      airtableName: 'rsv_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'rsv_treatments',
      airtableBase: 'lzdb',
    },
  ],
}

const RSVNewsPressReleases: RSVNavigationPage<
  typeof InfluenzaPressReleasesModel
> = {
  key: 'RSVNewsPressReleases',
  path: null,
  model: InfluenzaPressReleasesModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'front_end_export_rsv_default_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Vaccines',
      airtableName: 'front_end_export_rsv_vaccines_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'front_end_export_rsv_treatments_view',
      airtableBase: 'respiratory360',
    },
  ],
}
const RSVNewsMedia: RSVNavigationPage<typeof InfluenzaMediaModel> = {
  key: 'RSVNewsMedia',
  path: null,
  model: InfluenzaMediaModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'front_end_export_rsv_default_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Vaccines',
      airtableName: 'front_end_export_rsv_vaccines_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'front_end_export_rsv_treatments_view',
      airtableBase: 'respiratory360',
    },
  ],
}

const ApprovalsVaccines: RSVNavigationPage<typeof RsvApprovalsVaccinesModel> = {
  key: 'Vaccines',
  path: ['regulatory', 'table'],
  model: RsvApprovalsVaccinesModel,
  component: GenericTable,
  views: [
    {
      name: 'Vaccines',
      airtableName: 'rsv_regulatory_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'rsv_regulatory_treatments',
      airtableBase: 'lzdb',
      overrideModel: RsvApprovalsNonVaccinesModel,
    },
  ],
}

export const RSVPages: INavigationPages<RSVSlugs> = {
  RSVCandidatesVaccine,
  RSVResultsVaccines,
  RSVResultsNonVaccines,
  RSVTrials,
  RSVNewsPressReleases,
  RSVNewsMedia,
  ApprovalsVaccines,
}
